import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import {
  CountryCode,
  getCountryCallingCode,
  isSupportedCountry,
} from 'libphonenumber-js'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CountryPhoneDropdown from 'src/components/CountryPhoneDropdown/CountryPhoneDropdown'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import PhoneTextInputField from 'src/components/PhoneTextInputField/PhoneTextInputField'
import Popup from 'src/components/Popup/Popup'
import VerifyPhoneNumberPopup from 'src/components/VerifyPhoneNumberPopup/VerifyPhoneNumberPopup'
import { ImageNames } from 'src/enums'
import { useBackgroundDispatch, useFormFields } from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns, { useTranslate } from 'src/localization/Trns'
import { createSetIsUpdatePhoneNumberPopupOpen } from 'src/redux/actions/app.actions'
import {
  POST_SEND_PHONE_VERIFICATION_CODE_SUCCESS,
  postAuthSendPhoneVerificationCode,
  resetVerificationErrors,
} from 'src/redux/actions/auth.actions'
import {
  selectEmailFromState,
  selectNullSafeLocaleFromState,
  selectResendVerificationTopLevelErrorFromState,
  selectSendPhoneVerificationLoadingFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

type PhoneNumberFlatFields = {
  phoneNumber?: string
  phoneIsoCountry: string
}

interface UpdatePhoneNumberProps {
  className?: string
  loading?: boolean
  email?: string
  password?: string
}

const UpdatePhoneNumberPopup: React.FC<UpdatePhoneNumberProps> = (props) => {
  const dispatch = useBackgroundDispatch()
  const translate = useTranslate()
  const user = useSelector(selectUserListModelFromState)
  const email = user?.email ? user.email : useSelector(selectEmailFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const sendVerificationTopLevelError = useSelector(
    selectResendVerificationTopLevelErrorFromState,
  )
  const sendPhoneVerificationLoading = useSelector(
    selectSendPhoneVerificationLoadingFromState,
  )
  const buildingsImageSrc = useColorOrGrayscaleImage(ImageNames.Buildings)

  const appName = useSelector(selectAppName)

  const [displayVerifyPhoneNumberPopup, setDisplayVerifyPhoneNumberPopup] =
    useState<boolean>(false)

  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<PhoneNumberFlatFields>(
    {
      phoneIsoCountry: isSupportedCountry(locale.slice(-2).toUpperCase())
        ? (locale.slice(-2).toUpperCase() as CountryCode)
        : 'US',
    },
    () => ({
      phoneNumber: [stringIsEmpty()],
      phoneIsoCountry: [stringIsEmpty()],
    }),
    undefined,
  )

  const phoneNumber = `+${getCountryCallingCode(flatFields.phoneIsoCountry as CountryCode)}${flatFields.phoneNumber}`

  const dispatchPostSendPhoneVerificationCode = (): void => {
    if (flatFieldsSetErrors() && flatFields.phoneNumber) {
      dispatch(
        postAuthSendPhoneVerificationCode(props.email ?? email, phoneNumber),
      ).then((res): void => {
        if (res.type === POST_SEND_PHONE_VERIFICATION_CODE_SUCCESS) {
          setDisplayVerifyPhoneNumberPopup(true)
        }
      })
    }
  }

  return (
    <>
      {!displayVerifyPhoneNumberPopup ? (
        <Popup
          noPadding
          onClose={(): void => {
            dispatch(createSetIsUpdatePhoneNumberPopupOpen(false))
            dispatch(resetVerificationErrors())
          }}
        >
          <div
            css={css`
              position: relative;
              padding: 60px;
            `}
          >
            <img
              src={buildingsImageSrc}
              alt=""
              css={css`
                position: absolute;
                top: 0;
                right: 0;
                border-top-right-radius: 10px;
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  display: none;
                `)}
              `}
            />
            <div
              css={css`
                display: flex;
                flex-direction: column;
                ${getFontCss(
                  32,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
                margin-bottom: 20px;
              `}
            >
              <Trns source={'Add a phone number'} />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 12px;
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              <div>
                <Trns
                  source={
                    "After entering your phone number, you'll need to confirm the 6-digit verification code we send you."
                  }
                />
              </div>
              <div>
                {translate(
                  "By verifying your phone number, you agree to receive text messages from ${appName} for verification passcodes and account-related alerts. Message and data rates may apply.",
                  { appName: appName },
                )}
              </div>
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <div
                css={css`
                  margin-top: 40px;
                  display: flex;
                  flex-direction: column;
                `}
              >
                <FieldLabel>
                  <Trns source={'Phone Number'} />
                </FieldLabel>
                <div
                  css={css`
                    display: flex;
                    gap: 8px;
                    align-items: center;
                  `}
                >
                  <CountryPhoneDropdown
                    id={'updatePhoneNumberField'}
                    selectedValue={flatFields.phoneIsoCountry}
                    chooseOption={(val: string): void =>
                      onFlatFieldChange('phoneIsoCountry', val)
                    }
                  />
                  <PhoneTextInputField
                    id={'updatePhoneNumberVelue'}
                    css={css`
                      flex-grow: 2;
                    `}
                    errors={flatFieldsErrors['phoneNumber']}
                    value={flatFields.phoneNumber}
                    countryIso={flatFields.phoneIsoCountry}
                    onChange={(value: string): void =>
                      onFlatFieldChange('phoneNumber', value)
                    }
                  />
                </div>
              </div>
              <div
                css={css`
                  margin-top: 40px;
                  display: flex;
                  gap: 20px;
                  align-items: center;
                `}
              >
                <Button
                  css={css`
                    height: 40px;
                  `}
                  type={ButtonTypes.button}
                  design={ButtonDesigns.designCta}
                  big={true}
                  text={<Trns source={'Send Verification Code'} />}
                  onClick={(): void => {
                    dispatchPostSendPhoneVerificationCode()
                  }}
                  loading={sendPhoneVerificationLoading}
                />
                <HoverableLink
                  ariaLabelSource={'Cancel'}
                  onClick={(): void => {
                    dispatch(createSetIsUpdatePhoneNumberPopupOpen(false))
                    dispatch(resetVerificationErrors())
                  }}
                >
                  <Trns source={'Cancel'} />
                </HoverableLink>
              </div>
              {sendVerificationTopLevelError && (
                <ErrorList
                  errors={[sendVerificationTopLevelError]}
                  css={css`
                    margin-top: 8px;
                  `}
                />
              )}
            </div>
          </div>
        </Popup>
      ) : (
        <VerifyPhoneNumberPopup
          phoneNumberProfileSettingsFlow
          password={props.password}
          phoneNumber={phoneNumber}
        />
      )}
    </>
  )
}

export default UpdatePhoneNumberPopup
